import { useRef, useState } from 'react'
import { Container, CircularProgress, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import Spacebox from '../components/Spacebox'
import Flexbox from '../components/Flexbox'
import { useSelector } from 'react-redux'

// ─── helpers ──────────────────────────────────────────────────────────────────
function formatTs(ts) {
    if (!ts) return '—'
    return new Date(ts).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
    })
}

function statusColor(status) {
    if (status === 'success') return { bg: '#22c55e20', color: '#16a34a', border: '#22c55e40' }
    if (status === 'error') return { bg: '#ef444420', color: '#dc2626', border: '#ef444440' }
    return { bg: '#94a3b820', color: '#64748b', border: '#94a3b840' }
}

function actionLabel(action) {
    const map = {
        removed_from_workflow: 'Removed from Workflow',
        already_removed: 'Already Removed',
        skipped: 'Skipped',
        error: 'Error'
    }
    return map[action] ?? action
}

function SummaryChip({ label, value, color }) {
    return (
        <div style={{
            padding: '10px 16px', borderRadius: 8, background: `${color}15`,
            border: `1px solid ${color}30`, textAlign: 'center', minWidth: 90
        }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: '11px', opacity: .6, marginTop: 2 }}>{label}</div>
        </div>
    )
}

// ─── rows drawer for a single upload ─────────────────────────────────────────
function UploadRows({ uploadId, border, shade }) {
    const { data: rows, isLoading } = useQuery({
        queryFn: async () => {
            const res = await fetch(`/api/ndr-leads/${uploadId}/rows`, { credentials: 'include' })
            const json = await res.json()
            if (!json.success) throw new Error(json.msg)
            return json.data
        },
        queryKey: ['ndr-rows', uploadId],
        staleTime: 5 * 60 * 1000
    })

    if (isLoading) return (
        <div style={{ padding: '12px 16px' }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} variant="text" width="80%" style={{ marginBottom: 6 }} />)}
        </div>
    )

    if (!rows?.length) return <div style={{ padding: '12px 16px', opacity: .5 }}><small>No rows found.</small></div>

    return (
        <div style={{ overflowX: 'auto' }}>
            {/* header */}
            <div style={{
                display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1.4fr 2fr',
                padding: '8px 16px', background: shade, borderBottom: `1px solid ${border}`
            }}>
                {['Row', 'Contact ID', 'Disposition', 'Action', 'Message'].map(col => (
                    <div key={col} style={{ fontSize: '10px', fontWeight: 700, opacity: .5, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        {col}
                    </div>
                ))}
            </div>
            {/* rows */}
            {rows.map((row) => {
                const sc = statusColor(row.status)
                return (
                    <div key={row.id} style={{
                        display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1.4fr 2fr',
                        padding: '10px 16px', borderBottom: `1px solid ${border}`,
                        alignItems: 'center'
                    }}>
                        <small style={{ opacity: .5 }}>{row.row_number}</small>
                        <small style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all', paddingRight: 8 }}>
                            {row.contact_id || '—'}
                        </small>
                        <small style={{ opacity: .7 }}>{row.disposition || '—'}</small>
                        <div>
                            <span style={{
                                fontSize: '10px', fontWeight: 700, padding: '2px 7px',
                                borderRadius: 100, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`
                            }}>
                                {actionLabel(row.action)}
                            </span>
                        </div>
                        <small style={{ opacity: .65, lineHeight: 1.4 }}>{row.message}</small>
                    </div>
                )
            })}
        </div>
    )
}

// ─── single history card ──────────────────────────────────────────────────────
function HistoryCard({ upload, border, shade, headBg }) {
    const [open, setOpen] = useState(false)
    const s = upload.summary ?? {}

    return (
        <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
            {/* card header — clickable */}
            <div
                onClick={() => setOpen(o => !o)}
                style={{
                    padding: '14px 16px', cursor: 'pointer', background: headBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
                }}
            >
                <div style={{ flex: 1 }}>
                    <Flexbox alignItems="center" style={{ gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '14px' }}>{upload.filename}</span>
                        <span style={{ fontSize: '11px', opacity: .5 }}>{formatTs(upload.updated_at)}</span>
                    </Flexbox>
                    <Spacebox padding="6px" />
                    <Flexbox style={{ gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', opacity: .6 }}>Total: <b>{s.total ?? 0}</b></span>
                        <span style={{ fontSize: '11px', color: '#16a34a' }}>Removed: <b>{s.workflow_removed ?? 0}</b></span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>Already removed: <b>{s.already_removed ?? 0}</b></span>
                        <span style={{ fontSize: '11px', color: '#dc2626' }}>Errors: <b>{s.errors ?? 0}</b></span>
                    </Flexbox>
                </div>
                <span style={{ fontSize: '18px', opacity: .4 }}>{open ? '▲' : '▼'}</span>
            </div>

            {/* expandable rows */}
            {open && <UploadRows uploadId={upload.id} border={border} shade={shade} />}
        </div>
    )
}

// ─── main page ────────────────────────────────────────────────────────────────
const NdrLeadsPage = ({ title }) => {
    document.querySelector('title').innerHTML = title

    const mode = useSelector(state => state.darkmode.value)
    const border = mode.dark ? '#3a3a3a' : '#e4e6eb'
    const shade = mode.dark ? '#2a2a2a' : '#f9fafb'
    const headBg = mode.dark ? '#1e1e1e' : '#f0f2f5'

    const fileInputRef = useRef(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [uploadResult, setUploadResult] = useState(null) // { success, summary, filename, upload_id, msg }
    const [dragOver, setDragOver] = useState(false)

    const { data: history, isLoading: historyLoading, refetch } = useQuery({
        queryFn: async () => {
            const res = await fetch('/api/ndr-leads', { credentials: 'include' })
            const json = await res.json()
            if (!json.success) throw new Error(json.msg)
            return json.data
        },
        queryKey: ['ndr-uploads'],
        staleTime: 60 * 1000
    })

    const handleFilePick = (file) => {
        if (!file) return
        if (!file.name.match(/\.xlsx$/i)) {
            alert('Please select an .xlsx file.')
            return
        }
        setSelectedFile(file)
        setUploadResult(null)
    }

    const handleUpload = async () => {
        if (!selectedFile) return
        setUploading(true)
        setUploadResult(null)
        try {
            const formData = new FormData()
            formData.append('file', selectedFile)
            const res = await fetch('/api/ndr-leads/upload', {
                method: 'POST',
                credentials: 'include',
                body: formData
            })
            const json = await res.json()
            setUploadResult(json.success
                ? { success: true, ...json.data }
                : { success: false, msg: json.msg || 'Upload failed' }
            )
            if (json.success) {
                setSelectedFile(null)
                refetch()
            }
        } catch (err) {
            setUploadResult({ success: false, msg: err.message })
        } finally {
            setUploading(false)
        }
    }

    return (
        <div style={{ minHeight: '100dvh', paddingBottom: 60 }}>
            <Container maxWidth="lg">
                <Spacebox padding="20px" />
                <p style={{ fontWeight: 700, fontSize: '20px', margin: 0 }}>NDR Leads Upload</p>
                <small style={{ opacity: .5 }}>Upload an NDR xlsx sheet to process lead dispositions and remove contacted leads from the ATFCWorkflow.</small>

                <Spacebox padding="24px" />

                {/* ── upload zone ── */}
                <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => {
                        e.preventDefault()
                        setDragOver(false)
                        handleFilePick(e.dataTransfer.files[0])
                    }}
                    onClick={() => !selectedFile && fileInputRef.current?.click()}
                    style={{
                        border: `2px dashed ${dragOver ? '#0067CF' : border}`,
                        borderRadius: 12, padding: '32px 24px', textAlign: 'center',
                        cursor: selectedFile ? 'default' : 'pointer',
                        background: dragOver ? '#0067CF08' : shade,
                        transition: 'all .2s'
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        onChange={e => handleFilePick(e.target.files[0])}
                    />
                    {!selectedFile ? (
                        <>
                            <div style={{ fontSize: '32px', marginBottom: 8 }}>📂</div>
                            <p style={{ margin: 0, fontWeight: 600 }}>Drop an .xlsx file here or click to browse</p>
                            <small style={{ opacity: .5 }}>Column D = Contact ID · Column I = Disposition</small>
                        </>
                    ) : (
                        <Flexbox alignItems="center" justifyContent="center" style={{ gap: 12 }}>
                            <span style={{ fontSize: '24px' }}>📄</span>
                            <div style={{ textAlign: 'left' }}>
                                <p style={{ margin: 0, fontWeight: 600 }}>{selectedFile.name}</p>
                                <small style={{ opacity: .5 }}>{(selectedFile.size / 1024).toFixed(1)} KB</small>
                            </div>
                            <button
                                onClick={e => { e.stopPropagation(); setSelectedFile(null); setUploadResult(null) }}
                                style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', opacity: .5, fontSize: '18px' }}
                            >✕</button>
                        </Flexbox>
                    )}
                </div>

                <Spacebox padding="12px" />

                <Flexbox alignItems="center" style={{ gap: 10 }}>
                    <button
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading}
                        style={{
                            padding: '10px 24px', borderRadius: 8, border: 'none',
                            background: '#0067CF', color: 'white', fontWeight: 700,
                            cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer',
                            opacity: !selectedFile ? .4 : 1, fontSize: '14px',
                            display: 'flex', alignItems: 'center', gap: 8
                        }}
                    >
                        {uploading && <CircularProgress size={14} style={{ color: 'white' }} />}
                        {uploading ? 'Processing…' : 'Upload & Process'}
                    </button>
                    {uploading && <small style={{ opacity: .5 }}>This may take a moment for large sheets…</small>}
                </Flexbox>

                {/* ── upload result ── */}
                {uploadResult && (
                    <>
                        <Spacebox padding="16px" />
                        {uploadResult.success ? (
                            <div style={{ border: `1px solid #86efac`, borderRadius: 10, overflow: 'hidden' }}>
                                <div style={{ padding: '12px 16px', background: '#f0fdf4', borderBottom: '1px solid #86efac' }}>
                                    <Flexbox alignItems="center" style={{ gap: 8 }}>
                                        <span>✅</span>
                                        <span style={{ fontWeight: 700, color: '#15803d' }}>
                                            {uploadResult.filename} processed successfully
                                        </span>
                                    </Flexbox>
                                </div>
                                <div style={{ padding: '16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    <SummaryChip label="Total" value={uploadResult.summary.total} color="#64748b" />
                                    <SummaryChip label="Contacted" value={uploadResult.summary.contacted} color="#0067CF" />
                                    <SummaryChip label="Removed" value={uploadResult.summary.workflow_removed} color="#16a34a" />
                                    <SummaryChip label="Already Removed" value={uploadResult.summary.already_removed} color="#64748b" />
                                    <SummaryChip label="Disconnected" value={uploadResult.summary.disconnected} color="#f59e0b" />
                                    <SummaryChip label="Not Contacted" value={uploadResult.summary.not_contacted} color="#94a3b8" />
                                    <SummaryChip label="Errors" value={uploadResult.summary.errors} color="#dc2626" />
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                borderRadius: 8, padding: '12px 16px', background: '#fef2f2',
                                border: '1px solid #fca5a5', display: 'flex', gap: 10, alignItems: 'center'
                            }}>
                                <span>❌</span>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#dc2626' }}>{uploadResult.msg}</span>
                            </div>
                        )}
                    </>
                )}

                {/* ── history ── */}
                <Spacebox padding="32px" />
                <p style={{ fontWeight: 700, fontSize: '16px', margin: '0 0 16px' }}>Upload History</p>

                {historyLoading && (
                    [...Array(3)].map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={70} style={{ marginBottom: 10, borderRadius: 10 }} />
                    ))
                )}

                {!historyLoading && (!history || history.length === 0) && (
                    <div style={{ textAlign: 'center', opacity: .4, padding: '32px 0' }}>
                        <small>No uploads yet.</small>
                    </div>
                )}

                {!historyLoading && history?.map(upload => (
                    <HistoryCard
                        key={upload.id}
                        upload={upload}
                        border={border}
                        shade={shade}
                        headBg={headBg}
                    />
                ))}
            </Container>
        </div>
    )
}

export default NdrLeadsPage
